import {showToast} from "../components/general/CustomToast";

/**
 * Valida que el identificador no esté vacío y cumpla con el largo mínimo
 * requerido por el backend (InputSearchDto: @Size(min = 5)).
 *
 * @param identifier - Identificador a validar.
 * @returns true si el identificador es válido.
 * @author cfernandez
 * @version 1.0.0
 * @since 2026-04-08
 */
export const isValidIdentifier = (identifier?: string): boolean => {
    return !!identifier && identifier.trim().length >= 5;
}

export const capitalizeFirstLetter = (text: string): string => {
    if (!text) {
        return '';
    }

    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export function reemplaceValue(type: string | undefined, value: string, maxLength: number | undefined) {
    let filteredValue;
    if (!type) {
        return value;
    }
    switch (type) {
        case 'identification' :
            filteredValue = value.replace(/\D/g, '').slice(0, 10);
            return filteredValue;
        case 'telephone' :
            filteredValue = value.replace(/[^#0-9]/g, '').slice(0, maxLength ? maxLength : 500);
            return filteredValue;
        case 'integer' :
            filteredValue = value.replace(/[^-\d]/g, '').slice(0, maxLength ? maxLength : 500);
            return filteredValue;
        case 'number' :
            filteredValue = value.replace(/[^\d.]/g, '');
            return filteredValue;
        case 'mail' :
            filteredValue = value.replace(/[!"#$%&/()=?¡'*{}[\],><+`^~|°¿;]/g, '');
            return filteredValue;
        case 'letters' :
            if (maxLength !== undefined) {
                filteredValue = value.replace(/[!"#$%&/()=?¡'*{}[\],@_\-><`^~.:+°|¿;0-9]/g, '').slice(0, maxLength);
            } else {
                filteredValue = value.replace(/[!"#$%&/()=?¡'*{}[\],@_\-><`^~.:+°|¿;0-9]/g, '');
            }
            return filteredValue;
        case 'letters-number' :
            if (maxLength !== undefined) {
                filteredValue = value.replace(/[!"#$%&/()=?¡'*{}[\],@_\-><`^~.:+°|¿;]/g, '').slice(0, maxLength);
            } else {
                filteredValue = value.replace(/[!"#$%&/()=?¡'*{}[\],@_\-><`^~.:+°|¿;]/g, '');
            }
            return filteredValue;
        case 'text' :
            filteredValue = value.replace(/["#$&/()*{}[\]@\-><`^~+°|]/g, '');
            return filteredValue;
        default :
            return value;
    }
}

/**
 * Función `getTextByParameter`:
 *
 * Esta función obtiene un texto basado en el parámetro proporcionado. Si el valor es nulo o indefinido, se muestra un mensaje de error.
 *
 * @param {string} [value] - El valor de entrada que se desea evaluar.
 * @param {'number' | 'string' | 'boolean'} [parameterReturn] - El tipo de parámetro que se desea retornar.
 *
 * @returns - El valor de entrada si es válido, o un string vacío si hay un error.
 */
export function getTextByParameter(value?: string, parameterReturn?: 'number' | 'string' | 'boolean') {
    if(!value) {
        showToast('Error en configuración de parámetro', 'error')
        return ''
    }
    if (parameterReturn === 'number') {
        parseInt(value)
    }
    return value
}

/**
 * Función `getNumberByParameter`:
 *
 * Esta función convierte un string a un número. Si el valor es nulo o indefinido, retorna 0.
 *
 * @param {string} [value] - El valor de entrada que se desea convertir a número.
 *
 * @param valueDefaultReturn - valor por defecto si no se encuentra el parámetro de búsqueda.
 *
 * @returns El valor convertido a número, o 0 si el valor es nulo o indefinido.
 */
export function getNumberByParameter(value?: string, valueDefaultReturn?: number) {
    if(!value) {
        return valueDefaultReturn ? valueDefaultReturn : 0
    }
    return parseInt(value)
}

/**
 * Función `getBooleanByParameter`:
 *
 * Esta función convierte un string a un booleano. Si el valor es nulo o indefinido, se muestra un mensaje de error.
 *
 * @param value - El valor de entrada que se desea evaluar.
 * @param hiddenAlert - ocular alerta de variable de entorno.
 *
 * @returns `true` si el valor es 'true', `false` en caso contrario, o un string vacío si hay un error.
 */
export function getBooleanByParameter(value?: string, hiddenAlert?: boolean) {
    if(value === undefined) {
        if (!hiddenAlert) {
            showToast('Error en configuración de parámetro', 'error')
        }
        return false
    }
    return value.toLowerCase() === 'true'
}

/**
 * Función `getTextSpace`:
 *
 * Esta función convierte un string a su valor respetando los espacios.
 *
 * @param value - El valor de entrada.
 *
 * @returns string de entrada corregído.
 */
export function getTextSpace(value: string | undefined) {
    if (!value) {
        return '';
    }
    return value.replace(/ /g, "\u00A0");
}

/**
 * Función `validEmptyText`:
 *
 * Esta función retorna el valor en string.
 *
 * @param value - El valor de entrada.
 *
 * @returns string.
 */
export function validEmptyText(value: string | undefined | null): string {
    if (!value) {
        return '';
    }
    return value;
}


